/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { FocusEvent, MouseEvent, useMemo, useState } from 'react';

import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';

import { RenderedHTML } from './RenderedHTML';
import styles from './RenderedHtmlWithPopup.module.scss';

type RenderedHtmlPopupRule = {
  selector: string;
  getPopupHtml: (element: HTMLElement) => string | undefined;
};

type PopupState = {
  anchorEl: HTMLElement;
  html: string;
};

type RenderedHtmlWithPopupProps = React.ComponentProps<typeof RenderedHTML> & {
  popupRules?: RenderedHtmlPopupRule[];
};

function getPopupStateFromElement(
  target: EventTarget | null,
  popupRules: RenderedHtmlPopupRule[] | undefined
): PopupState | null {
  const htmlTarget = target instanceof HTMLElement ? target : null;
  if (!htmlTarget || !popupRules?.length) return null;

  for (const rule of popupRules) {
    const matchedElement = htmlTarget.closest(rule.selector);
    if (!(matchedElement instanceof HTMLElement)) continue;

    const html = rule.getPopupHtml(matchedElement)?.trim();
    if (!html) continue;

    return { anchorEl: matchedElement, html };
  }

  return null;
}

export function RenderedHtmlWithPopup({ popupRules, ...renderedHtmlProps }: RenderedHtmlWithPopupProps) {
  const normalizedPopupRules = useMemo(() => popupRules?.filter((rule) => !!rule.selector) || [], [popupRules]);
  const [popupState, setPopupState] = useState<PopupState | null>(null);

  function maybeOpenPopup(target: EventTarget | null) {
    const nextState = getPopupStateFromElement(target, normalizedPopupRules);
    setPopupState((currentState) => {
      if (!nextState) return currentState;
      if (currentState?.anchorEl === nextState.anchorEl && currentState.html === nextState.html) return currentState;
      return nextState;
    });
  }

  function maybeClosePopupFromTarget(target: EventTarget | null, relatedTarget: EventTarget | null) {
    if (!popupState) return;

    const targetElement = target instanceof HTMLElement ? target : null;
    const relatedElement = relatedTarget instanceof HTMLElement ? relatedTarget : null;

    if (relatedElement && popupState.anchorEl.contains(relatedElement)) return;
    if (targetElement && popupState.anchorEl.contains(targetElement)) {
      setPopupState(null);
      return;
    }
    if (!targetElement) {
      setPopupState(null);
    }
  }

  function maybeClosePopup(event: MouseEvent<HTMLDivElement> | FocusEvent<HTMLDivElement>) {
    const relatedTarget = event.relatedTarget instanceof HTMLElement ? event.relatedTarget : null;
    if (relatedTarget && popupState?.anchorEl?.contains(relatedTarget)) return;
    setPopupState(null);
  }

  return (
    <div
      className={styles.wrapper}
      onFocus={(event) => maybeOpenPopup(event.target)}
      onMouseOut={(event) => maybeClosePopupFromTarget(event.target, event.relatedTarget)}
      onMouseOver={(event) => maybeOpenPopup(event.target)}
      onBlur={maybeClosePopup}
    >
      <RenderedHTML {...renderedHtmlProps} />
      <Popper open={!!popupState} anchorEl={popupState?.anchorEl} placement="bottom-start">
        {popupState && (
          <Paper className={styles.popupPaper} elevation={3}>
            <div className={styles.popupContent} dangerouslySetInnerHTML={{ __html: popupState.html }} />
          </Paper>
        )}
      </Popper>
    </div>
  );
}
